import { useGameStore } from '@/stores/gameStore';
import { formatNumber } from '@/utils/gameUtils';
import type { Action } from '@/types';

// ============================================================================
// ACTION BUILDER UTILITY
// ============================================================================

export interface ActionConfig {
  id: string;
  text: string;
  handler: string;
  secondary?: boolean;
  requiresUpgrade?: boolean;
  upgradeRequired?: string;
  visible?: boolean;
  tooltip?: string;
}

export interface InfrastructureConfig {
  id: string;
  name: string;
  upgradeRequired: string;
  stateProperty: string;
}

export interface SellActionConfig {
  id: string;
  text: string;
  handler: string;
  sellValue: number;
  resourceType: string;
  tooltip: string;
}

export class ActionBuilder {
  private store = useGameStore();

  /**
   * Build basic actions from configuration
   */
  buildBasicActions(configs: ActionConfig[]): Action[] {
    return configs.map(config => ({
      id: config.id,
      text: this.resolveText(config),
      handler: this.getHandler(config.handler),
      secondary: config.secondary || false,
      requiresUpgrade: config.requiresUpgrade || false,
      visible: this.resolveVisibility(config),
      tooltip: config.tooltip
    }));
  }

  /**
   * Build infrastructure actions from infrastructure IDs
   */
  buildInfrastructureActions(infrastructureIds: string[]): Action[] {
    return infrastructureIds
      .map(id => {
        const infrastructure = this.store.entityManager.getInfrastructureById(id);
        if (!infrastructure) return {};

        // Show infrastructure if it meets non-personnel requirements
        if (!infrastructure.meetsNonPersonnelRequirements(this.store.state)) return null;

        // Get current count
        const propertyName = infrastructure.getStatePropertyName();
        const currentCount = this.store.state.infrastructure[propertyName] || 0;

        // Build text with count if maxCount exists
        let text = `${infrastructure.name} ($${formatNumber(infrastructure.cost)})`;
        if (infrastructure.maxCount !== undefined) {
          text = `${infrastructure.name} (${currentCount}/${infrastructure.maxCount}) - $${formatNumber(infrastructure.cost)}`;
        }

        // Build tooltip with missing requirements
        const missingRequirements = infrastructure.getMissingRequirements(this.store.state);
        let tooltip = infrastructure.description;
        if (missingRequirements.length > 0) {
          tooltip += '\n\nMissing:\n' + missingRequirements.map(req => `• ${req}`).join('\n');
        }

        return {
          id: `buy${this.capitalizeFirst(id)}`,
          text,
          handler: () => this.store.buyInfrastructure(id),
          secondary: true,
          requiresUpgrade: true,
          visible: infrastructure.meetsNonPersonnelRequirements(this.store.state),
          tooltip
        };
      })
      .filter(Boolean) as Action[];
  }

  /**
   * Build all actions for a pipeline stage
   */
  buildStageActions(
    basicConfigs: ActionConfig[], 
    infrastructureIds: string[]
  ): Action[] {
    const basicActions = this.buildBasicActions(basicConfigs);
    const infrastructureActions = this.buildInfrastructureActions(infrastructureIds);
    
    return [...basicActions, ...infrastructureActions];
  }

  /**
   * Resolve dynamic text values
   */
  private resolveText(config: ActionConfig): string {
    let text = config.text;
    
    // Replace dynamic values
    text = text.replace('$0.5', `$${this.store.RAW_DATA_SELL_VALUE}`);
    text = text.replace('$1', `$${this.store.CLEAN_DATA_SELL_VALUE}`);
    
    return text;
  }

  /**
   * Get handler function from string name
   */
  private getHandler(handlerName: string): () => void {
    const handlers: Record<string, () => void> = {
      collectRawData: () => this.store.collectRawData(),
      sellRawData: () => this.store.sellRawData(),
      cleanData: () => this.store.cleanData(),
      sellCleanData: () => this.store.sellCleanData(),
    };

    return handlers[handlerName] || (() => {});
  }

  /**
   * Resolve visibility based on upgrade requirements
   */
  private resolveVisibility(config: ActionConfig): boolean {
    if (config.visible !== undefined) {
      return config.visible;
    }

    if (config.requiresUpgrade && config.upgradeRequired) {
      return this.store.hasUpgrade(config.upgradeRequired);
    }

    return true;
  }

  /**
   * Capitalize first letter for ID generation
   */
  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

// ============================================================================
// FACTORY FUNCTION
// ============================================================================

export function createActionBuilder(): ActionBuilder {
  return new ActionBuilder();
} 